import { useCallback } from 'react'
import { isEmpty } from 'lodash'
import { TEndorsementsSchema } from 'transfix-form/forms/CarrierFleetInformation/components/EndorsementsModal/schema/EndorsementsSchema'
import { IUseCarrierFleetInformationSubmitter } from 'transfix-form/forms/CarrierFleetInformation/types'
import { useGlobalToast } from 'transfix-ui/components/Toast/GlobalToast'
import { useCarrierProfileQuery } from 'apps/carrier/components/Carrier/graphql/CarrierProfile'
import { TOAST_ERROR_MESSAGE } from '../../../constants'
import { useUpdateCarrierEndorsementsMutation } from '../graphql/UpdateCarrierProfileEndorsementsMutation'
import { getUpdateCarrierProfileEndorsementsInputs } from '../utils/getUpdateCarrierProfileEndorsementsInputs'

const TOAST_SUCCESS_MESSAGE = 'Endorsements updated successfully'

export const useEndorsementsSubmitter = (
  carrierApplicationId: string
): IUseCarrierFleetInformationSubmitter<TEndorsementsSchema> => {
  const { openToast } = useGlobalToast()
  const {
    loading: fetchCarrierProfileLoading,
    refetch: fetchCarrierProfile,
  } = useCarrierProfileQuery({
    skip: true,
    variables: {
      id: carrierApplicationId,
    },
  })
  const [
    updateCarrierEndorsementsMutation,
    { loading: updateCarrierEndorsementsMutationLoading },
  ] = useUpdateCarrierEndorsementsMutation()
  const showErrorToast = useCallback(() => {
    openToast({ toastMessage: TOAST_ERROR_MESSAGE, toastType: 'alert' })
  }, [openToast])

  const submitHandler = useCallback(
    async (formValues: TEndorsementsSchema) => {
      const { data, errors: gqlErrors } = await updateCarrierEndorsementsMutation({
        variables: {
          input: getUpdateCarrierProfileEndorsementsInputs({ carrierApplicationId, formValues }),
        },
      })

      const { updateCarrierEndorsements } = data ?? {}
      const { errors: clientErrors } = updateCarrierEndorsements ?? {}

      if (isEmpty(gqlErrors) && isEmpty(clientErrors)) {
        await fetchCarrierProfile()
        openToast({ toastMessage: TOAST_SUCCESS_MESSAGE })
        return true
      }
      showErrorToast()
      return false
    },
    [
      carrierApplicationId,
      fetchCarrierProfile,
      openToast,
      showErrorToast,
      updateCarrierEndorsementsMutation,
    ]
  )

  return {
    loading: fetchCarrierProfileLoading || updateCarrierEndorsementsMutationLoading,
    submitHandler,
  }
}
